import React, { useMemo, type ReactNode } from 'react';
// eslint-disable-next-line no-restricted-imports -- Retains the existing readers' control path during migration.
import { Subscribe } from 'unstated';

import { expValEquals } from '@confluence/feature-experiments';

import { BannerStateContainer } from './BannerStateContainer.ts';
import { useBannerHeights } from './useBannerState.ts';

/** The read-only part of the legacy API, also accepted by components passed a height reader. */
export type BannerHeightState = {
	getTotalHeight: (includeTopNav?: boolean) => number;
};

type BannerHeightReaderProps = {
	children: (state: BannerHeightState) => ReactNode;
};

const BANNER_CONTAINERS = [BannerStateContainer];

const BannerHeightReaderWithHooks = ({ children }: BannerHeightReaderProps) => {
	const { bannerHeight, totalHeight } = useBannerHeights();
	const state = useMemo<BannerHeightState>(
		() => ({
			getTotalHeight: (includeTopNav = true) => (includeTopNav ? totalHeight : bannerHeight),
		}),
		[bannerHeight, totalHeight],
	);

	return <>{children(state)}</>;
};

/**
 * Migration boundary for height-only readers, including class components and render props.
 * Only treatment mounts the hooks; control retains the injected container and its configuration.
 * The narrow read-only API lets callers retain their existing navigation-offset calculations.
 */
export const BannerHeightReader = ({ children }: BannerHeightReaderProps): React.JSX.Element => {
	if (expValEquals('confluence_banner_store_migration', 'isEnabled', true)) {
		return <BannerHeightReaderWithHooks>{children}</BannerHeightReaderWithHooks>;
	}

	return (
		<Subscribe to={BANNER_CONTAINERS}>
			{(state: BannerStateContainer) => <>{children(state)}</>}
		</Subscribe>
	);
};
